import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Newspaper, Plus, Edit3, Trash2, Save, X, Upload, ImageIcon, Loader2, User, Calendar, ExternalLink,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  'Lanzamiento', 'Artista Destacado', 'Colaboracion',
  'Industria', 'Tips', 'Playlists', 'Evento', 'General',
];

const emptyForm = {
  title: '',
  excerpt: '',
  content: '',
  category: 'Lanzamiento',
  image: '',
  artist_id: '',
  artist_name: '',
  link_url: '',
};

const NewsPanel = () => {
  const [news, setNews] = useState([]);
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null); // null=no edit, 'new'=create, id=edit existing
  const [form, setForm] = useState(emptyForm);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const [n, a] = await Promise.all([
        axios.get(`${API_URL}/api/admin/news`, { withCredentials: true }),
        axios.get(`${API_URL}/api/artists/`).catch(() => ({ data: [] })),
      ]);
      setNews(n.data);
      setArtists(a.data || []);
    } catch {}
    setLoading(false);
  };

  const openNew = () => { setForm(emptyForm); setEditingId('new'); };
  const openEdit = (n) => {
    setForm({
      title: n.title || '',
      excerpt: n.excerpt || '',
      content: n.content || '',
      category: n.category || 'Lanzamiento',
      image: n.image || '',
      artist_id: n.artist_id || '',
      artist_name: n.artist_name || '',
      link_url: n.link_url || '',
    });
    setEditingId(n.id);
  };
  const cancel = () => { setEditingId(null); setForm(emptyForm); };

  const onArtistChange = (id) => {
    const a = artists.find(x => x.id === id);
    setForm(f => ({ ...f, artist_id: id, artist_name: a?.name || '' }));
  };

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API_URL}/api/admin/news/upload-image`, fd, { withCredentials: true, timeout: 60000 });
      setForm(f => ({ ...f, image: r.data.url }));
    } catch (e) {
      alert(e.response?.data?.detail || 'Error subiendo imagen');
    }
    setUploading(false);
  };

  const save = async () => {
    if (!form.title.trim() || !form.excerpt.trim() || !form.image.trim()) {
      alert('Titulo, resumen e imagen son requeridos');
      return;
    }
    setSaving(true);
    try {
      if (editingId === 'new') {
        await axios.post(`${API_URL}/api/admin/news`, form, { withCredentials: true });
      } else {
        await axios.put(`${API_URL}/api/admin/news/${editingId}`, form, { withCredentials: true });
      }
      await load();
      cancel();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error guardando');
    }
    setSaving(false);
  };

  const remove = async (id) => {
    if (!window.confirm('Eliminar esta noticia?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/news/${id}`, { withCredentials: true });
      await load();
    } catch {}
  };

  if (loading) return <div className="p-6"><Loader2 className="w-5 h-5 animate-spin text-[#E8720C]" /></div>;

  return (
    <div className="space-y-5" data-testid="news-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Newspaper className="w-5 h-5 text-[#E8720C]" /> Noticias y Lanzamientos
          </h2>
          <p className="text-xs text-white/40 mt-1">Manejador de noticias que aparecen en la homepage publica</p>
        </div>
        {editingId === null && (
          <button onClick={openNew}
            className="px-4 py-2 bg-[#E8720C] hover:bg-[#C55A00] text-white rounded-lg text-sm font-bold flex items-center gap-2"
            data-testid="create-news-btn">
            <Plus className="w-4 h-4" /> Nueva noticia
          </button>
        )}
      </div>

      {/* Form */}
      {editingId && (
        <div className="glass-card p-5 border border-[#E8720C]/20" data-testid="news-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold uppercase tracking-wider text-[#E8720C]">
              {editingId === 'new' ? 'Nueva noticia' : 'Editar noticia'}
            </h3>
            <button onClick={cancel}><X className="w-4 h-4 text-white/50" /></button>
          </div>

          <div className="space-y-3">
            <div>
              <label className="text-[11px] text-white/50 mb-1 block uppercase tracking-wider">Titulo *</label>
              <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                className="w-full input-glass text-sm" placeholder="Nombre de la noticia"
                data-testid="news-title" />
            </div>
            <div>
              <label className="text-[11px] text-white/50 mb-1 block uppercase tracking-wider">Resumen corto *</label>
              <textarea value={form.excerpt} onChange={e => setForm({ ...form, excerpt: e.target.value })}
                className="w-full input-glass text-sm" rows={2}
                placeholder="1-2 lineas que resumen la noticia"
                data-testid="news-excerpt" />
            </div>
            <div>
              <label className="text-[11px] text-white/50 mb-1 block uppercase tracking-wider">Contenido completo</label>
              <textarea value={form.content} onChange={e => setForm({ ...form, content: e.target.value })}
                className="w-full input-glass text-sm" rows={5}
                placeholder="Texto completo (opcional)"
                data-testid="news-content" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] text-white/50 mb-1 block uppercase tracking-wider">Categoria *</label>
                <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                  className="w-full input-glass text-sm" data-testid="news-category">
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] text-white/50 mb-1 block uppercase tracking-wider">Artista relacionado</label>
                <select value={form.artist_id} onChange={e => onArtistChange(e.target.value)}
                  className="w-full input-glass text-sm" data-testid="news-artist">
                  <option value="">- Sin artista -</option>
                  {artists.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="text-[11px] text-white/50 mb-1 block uppercase tracking-wider">URL externa (opcional)</label>
              <input value={form.link_url} onChange={e => setForm({ ...form, link_url: e.target.value })}
                className="w-full input-glass text-sm" placeholder="https://open.spotify.com/..."
                data-testid="news-link" />
            </div>

            <div>
              <label className="text-[11px] text-white/50 mb-1 block uppercase tracking-wider">Imagen de portada *</label>
              <input ref={fileRef} type="file" accept="image/*" hidden
                onChange={(e) => uploadImage(e.target.files[0])} />
              <div className="flex items-center gap-3">
                {form.image ? (
                  <div className="relative">
                    <img src={form.image.startsWith('http') ? form.image : `${API_URL}${form.image}`}
                      alt="cover" className="w-28 h-20 object-cover rounded-lg border border-white/10" />
                    <button onClick={() => setForm({ ...form, image: '' })}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <div className="w-28 h-20 bg-white/[0.02] border border-dashed border-white/15 rounded-lg flex items-center justify-center">
                    <ImageIcon className="w-6 h-6 text-white/20" />
                  </div>
                )}
                <button onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="px-3 py-2 bg-white/5 hover:bg-white/10 text-white/80 rounded-lg text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50"
                  data-testid="news-upload-image">
                  {uploading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Subiendo...</> : <><Upload className="w-3.5 h-3.5" /> {form.image ? 'Cambiar' : 'Subir imagen'}</>}
                </button>
                <span className="text-[10px] text-white/30">O pega una URL:</span>
                <input value={form.image} onChange={e => setForm({ ...form, image: e.target.value })}
                  className="flex-1 input-glass text-xs py-1.5" placeholder="https://..." />
              </div>
            </div>
          </div>

          <div className="flex gap-2 mt-5">
            <button onClick={save} disabled={saving}
              className="px-5 py-2.5 bg-[#E8720C] hover:bg-[#C55A00] text-white rounded-lg text-sm font-bold flex items-center gap-2 disabled:opacity-50"
              data-testid="save-news-btn">
              {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Guardando...</> : <><Save className="w-4 h-4" /> Guardar</>}
            </button>
            <button onClick={cancel}
              className="px-4 py-2.5 bg-white/5 hover:bg-white/10 text-white/70 rounded-lg text-sm">Cancelar</button>
          </div>
        </div>
      )}

      {/* List */}
      {news.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <Newspaper className="w-10 h-10 text-white/20 mx-auto mb-3" />
          <p className="text-sm text-white/40">Aun no hay noticias publicadas</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {news.map((n) => (
            <div key={n.id} className="glass-card overflow-hidden flex flex-col" data-testid={`news-item-${n.id}`}>
              <div className="relative h-40 bg-white/5">
                <img src={n.image?.startsWith('http') ? n.image : `${API_URL}${n.image}`}
                  alt={n.title} className="w-full h-full object-cover" />
                <span className="absolute top-3 left-3 px-2.5 py-1 bg-black/60 backdrop-blur-sm text-[10px] font-semibold rounded-full">
                  {n.category}
                </span>
              </div>
              <div className="p-4 flex-1 flex flex-col">
                <h3 className="text-sm font-semibold line-clamp-2 mb-1">{n.title}</h3>
                <p className="text-xs text-white/50 line-clamp-2 mb-3">{n.excerpt}</p>
                <div className="flex items-center gap-2 text-[10px] text-white/40 mb-3 flex-wrap">
                  {n.artist_name && <span className="flex items-center gap-1"><User className="w-2.5 h-2.5" /> {n.artist_name}</span>}
                  <span className="flex items-center gap-1"><Calendar className="w-2.5 h-2.5" /> {new Date(n.created_at).toLocaleDateString()}</span>
                  {n.link_url && <a href={n.link_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-[#E8720C] hover:text-[#FF8C2E]"><ExternalLink className="w-2.5 h-2.5" /> Link</a>}
                </div>
                <div className="mt-auto flex gap-2">
                  <button onClick={() => openEdit(n)}
                    className="flex-1 px-3 py-1.5 bg-white/5 hover:bg-white/10 text-white/80 rounded-lg text-xs flex items-center justify-center gap-1.5"
                    data-testid={`edit-news-${n.id}`}>
                    <Edit3 className="w-3 h-3" /> Editar
                  </button>
                  <button onClick={() => remove(n.id)}
                    className="px-3 py-1.5 bg-red-500/15 hover:bg-red-500/25 text-red-400 rounded-lg text-xs flex items-center gap-1.5"
                    data-testid={`delete-news-${n.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NewsPanel;
